import React, { useState } from 'react';

import { Box, Collapse, Typography } from '@mui/material';

import { SectionHeading } from './SectionHeading';
import { classes } from './styles';
import FormSelectField from '../NewUI/Components/CustomUIElements/FormSelectField';

import type { SourceOption } from './types';

interface SourceSelectionProps {
  onSourceChange?: (value: string) => void;
  sourceValue?: string;
  sources?: SourceOption[];
  sourceError?: string;
  fontFamily?: string;
}

export const SourceSelection: React.FC<SourceSelectionProps> = ({
  onSourceChange,
  sourceValue: initialSourceValue = '',
  sources = [],
  sourceError = '',
  fontFamily
}) => {
  const [selectedSourceId, setSelectedSourceId] = useState<string>(initialSourceValue);

  const sourceOptions = sources.map((s) => ({
    value: String(s.id),
    label: s.name
  }));

  const handleSourceChange = (value: string | number) => {
    const stringValue = String(value);
    setSelectedSourceId(stringValue);
    onSourceChange?.(stringValue);
  };

  return (
    <Box
      aria-labelledby="source-selection-heading"
      component="section"
      sx={classes.sourceSelection.containerSx()}
    >
      <SectionHeading fontFamily={fontFamily} id="source-selection-heading">
        Source Selection
      </SectionHeading>

      <Box
        aria-labelledby="source-field-label"
        role="group"
        sx={classes.sourceSelection.sourceFieldContainerSx()}
      >
        <Typography
          component="label"
          htmlFor="source-input"
          id="source-field-label"
          sx={classes.sourceSelection.fieldLabelSx()}
        >
          Where did you hear about this job?
          <span aria-label="required" style={classes.shared.requiredIndicatorStyle}>
            {' '}
            *
          </span>
        </Typography>

        <FormSelectField
          required
          defaultValue={initialSourceValue}
          id="source-input"
          onChange={handleSourceChange}
          options={sourceOptions}
          styles={classes.shared.selectFieldStyles(!!sourceError)}
        />

        {/* Submit selected source id in the Rails form payload. */}
        <input
          id="application_source_id"
          name="application[source_id]"
          type="hidden"
          value={selectedSourceId}
        />

        {/* unmountOnExit re-mounts the element so role="alert" announces each time. */}
        <Collapse unmountOnExit in={!!sourceError}>
          <Box data-validation-error role="alert" sx={classes.sourceSelection.sourceErrorSx()}>
            {sourceError}
          </Box>
        </Collapse>
      </Box>

      {/* TODO: Close remaining parity gaps with legacy source behavior:
          1) reset stale source_id values from edit tokens
          2) show a clear reselect error if a source disappears mid-session
          3) avoid header horizontal shift when dropdown opens (scroll-lock/padding-right side effect) */}
    </Box>
  );
};

export default SourceSelection;
